import { config } from '../config.js';
import type { ApiResponse, AuthResponse, UserPublic, UserSession, Link, PageConfig, AnalyticsEvent, AnalyticsStats, Notification } from '../shared/types.js';

const TOKEN_KEY = 'auth_token';

class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.apiUrl;
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const token = this.getToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (response.status === 429) {
        const retryAfter = parseInt(response.headers.get('Retry-After') || '60', 10);
        return {
          success: false,
          error: data.error || 'Too many requests. Please slow down.',
          status: 429,
          rateLimited: true,
          retryAfter,
        };
      }

      if (!response.ok) {
        return { success: false, error: data.error || 'Request failed', status: response.status };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: (error as Error).message };
    }
  }

  async register(
    email: string,
    password: string,
    name: string,
    accountType: 'personal' | 'company' = 'personal',
    companyName?: string
  ): Promise<ApiResponse<AuthResponse>> {
    const result = await this.request<AuthResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, accountType, companyName }),
    });

    if (result.success && result.data) {
      this.setToken(result.data.token);
    }

    return result;
  }

  async login(email: string, password: string): Promise<ApiResponse<AuthResponse>> {
    const result = await this.request<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (result.success && result.data) {
      this.setToken(result.data.token);
    }

    return result;
  }

  async logout(): Promise<ApiResponse<{ message: string }>> {
    const result = await this.request<{ message: string }>('/api/auth/logout', {
      method: 'POST',
    });
    this.clearToken();
    return result;
  }

  async logoutAllSessions(): Promise<ApiResponse<{ message: string }>> {
    const result = await this.request<{ message: string }>('/api/auth/logout-all', {
      method: 'POST',
    });
    this.clearToken();
    return result;
  }

  async verifyEmail(token: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>('/api/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async resendVerificationEmail(): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>('/api/auth/resend-verification', {
      method: 'POST',
    });
  }

  async getSession(): Promise<ApiResponse<UserSession>> {
    return this.request<UserSession>('/api/auth/session');
  }

  async getProfile(slug: string): Promise<ApiResponse<UserPublic>> {
    return this.request<UserPublic>(`/api/users/${slug}`);
  }

  async updateProfile(data: Partial<{ name: string; companyName: string }>): Promise<ApiResponse<UserSession>> {
    return this.request<UserSession>('/api/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<{ message: string }>> {
    const result = await this.request<{ message: string }>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    // Clear token since all sessions are revoked after password change
    if (result.success) {
      this.clearToken();
    }

    return result;
  }

  async forgotPassword(email: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>('/api/auth/forgot', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, password: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>('/api/auth/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  async checkSlugAvailability(slug: string): Promise<ApiResponse<{ available: boolean; reason?: string }>> {
    return this.request<{ available: boolean; reason?: string }>(`/api/auth/check-slug?slug=${encodeURIComponent(slug)}`);
  }

  async updateSlug(slug: string): Promise<ApiResponse<{ message: string; user: UserSession }>> {
    return this.request<{ message: string; user: UserSession }>('/api/auth/update-slug', {
      method: 'POST',
      body: JSON.stringify({ slug }),
    });
  }

  async getLinks(): Promise<ApiResponse<Link[]>> {
    return this.request<Link[]>('/api/links');
  }

  async getPublicLinks(slug: string): Promise<ApiResponse<Link[]>> {
    return this.request<Link[]>(`/api/users/${slug}/links`);
  }

  async createLink(data: { title: string; url: string; icon?: string }): Promise<ApiResponse<Link>> {
    return this.request<Link>('/api/links', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLink(id: string, data: Partial<Link>): Promise<ApiResponse<Link>> {
    return this.request<Link>(`/api/links/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteLink(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/links/${id}`, {
      method: 'DELETE',
    });
  }

  async reorderLinks(linkIds: string[]): Promise<ApiResponse<Link[]>> {
    return this.request<Link[]>('/api/links/reorder', {
      method: 'POST',
      body: JSON.stringify({ linkIds }),
    });
  }

  async getPageConfig(): Promise<ApiResponse<PageConfig>> {
    return this.request<PageConfig>('/api/page');
  }

  async getPublicPageConfig(slug: string): Promise<ApiResponse<PageConfig>> {
    return this.request<PageConfig>(`/api/users/${slug}/page`);
  }

  async updatePageConfig(data: Partial<PageConfig>): Promise<ApiResponse<PageConfig>> {
    return this.request<PageConfig>('/api/page', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async generateQRCode(slug: string): Promise<ApiResponse<{ svg: string; dataUrl: string }>> {
    return this.request<{ svg: string; dataUrl: string }>(`/api/qr/${slug}`);
  }

  async getLogs(options?: {
    level?: string;
    category?: string;
    limit?: number;
    offset?: number;
    search?: string;
  }): Promise<ApiResponse<LogEntry[]>> {
    const params = new URLSearchParams();
    if (options?.level) params.set('level', options.level);
    if (options?.category) params.set('category', options.category);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.search) params.set('search', options.search);

    const queryString = params.toString();
    return this.request<LogEntry[]>(`/api/admin/logs${queryString ? `?${queryString}` : ''}`);
  }

  async getLogMetrics(): Promise<ApiResponse<LogMetrics>> {
    return this.request<LogMetrics>('/api/admin/logs/metrics');
  }

  async getLogCategories(): Promise<ApiResponse<string[]>> {
    return this.request<string[]>('/api/admin/logs/categories');
  }

  async getAdminUsers(): Promise<ApiResponse<Array<{ id: string; name: string; email: string }>>> {
    return this.request<Array<{ id: string; name: string; email: string }>>('/api/admin/users');
  }

  async trackAnalytics(
    userId: string,
    eventType: 'page_view' | 'link_click',
    options: { linkId?: string; referrer?: string } = {}
  ): Promise<ApiResponse<AnalyticsEvent>> {
    return this.request<AnalyticsEvent>('/api/analytics/track', {
      method: 'POST',
      body: JSON.stringify({ userId, eventType, ...options }),
    });
  }

  async getAnalyticsStats(days: number = 30): Promise<ApiResponse<AnalyticsStats>> {
    return this.request<AnalyticsStats>(`/api/analytics/stats?days=${days}`);
  }

  async sendContactMessage(data: { name: string; email: string; message: string }): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>('/api/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Notifications
  async getNotifications(limit: number = 50): Promise<ApiResponse<Notification[]>> {
    return this.request<Notification[]>(`/api/notifications?limit=${limit}`);
  }

  async markNotificationAsRead(notificationId: string): Promise<ApiResponse<{ success: boolean; hasUnreadNotifications: boolean }>> {
    return this.request<{ success: boolean; hasUnreadNotifications: boolean }>(`/api/notifications/${notificationId}/read`, {
      method: 'PATCH',
    });
  }

  async markAllNotificationsAsRead(): Promise<ApiResponse<{ success: boolean; count: number; hasUnreadNotifications: boolean }>> {
    return this.request<{ success: boolean; count: number; hasUnreadNotifications: boolean }>('/api/notifications/mark-all-read', {
      method: 'POST',
    });
  }

  async deleteNotification(notificationId: string): Promise<ApiResponse<{ success: boolean; hasUnreadNotifications: boolean }>> {
    return this.request<{ success: boolean; hasUnreadNotifications: boolean }>(`/api/notifications/${notificationId}`, {
      method: 'DELETE',
    });
  }

  async createNotification(data: {
    userId: string;
    type: 'welcome' | 'milestone' | 'admin' | 'analytics';
    title: string;
    message: string;
    actionUrl?: string;
  }): Promise<ApiResponse<Notification>> {
    return this.request<Notification>('/api/notifications', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async prepareUpload(
    imageType: 'profile' | 'background' | 'featured',
    filename: string,
    contentType: string
  ): Promise<ApiResponse<{ url: string; fields: Record<string, string>; key: string }>> {
    return this.request<{ url: string; fields: Record<string, string>; key: string }>('/api/storage/prepare-upload', {
      method: 'POST',
      body: JSON.stringify({ imageType, filename, contentType }),
    });
  }

  getStorageUrl(key: string): string {
    return `${this.baseUrl}/api/storage/${encodeURIComponent(key)}`;
  }
}

export interface LogEntry {
  id: string;
  timestamp: string;
  level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';
  category: string;
  message: string;
  meta?: Record<string, unknown>;
}

export interface LogMetrics {
  total: number;
  byLevel: Record<string, number>;
  byCategory: Record<string, number>;
  recentErrors: number;
  requestsPerMinute: number;
  userRequestsPerMinute: number;
}

export const api = new ApiClient();
